import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getServices, saveService } from "../../components/Api/GmsService";
import { SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission, 
  hasDeletePermissionWithAdmin, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

const defaultForm = {
  service_Id: 0,
  service_NameEN: "",
  service_NameAR: "",
  description: "",
  status: true,
  created_By: 1,
  created_Date: new Date().toISOString(),
  modified_By: 1,
  modified_Date: new Date().toISOString(),
};

const emptyFilter = {
  service_NameEN: "",
  status: null,
};

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

export default function ServicePage() {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsService');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsService');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsService');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsService');
  const isAdmin = isUserAdmin();

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  const formatDateTime = (dateTimeStr) => {
    try {
      return dateTimeStr.split('T')[0];
    } catch (error) {
      return "N/A";
    }
  };

  const formatFullDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  useEffect(() => {
    console.log('Component mounted');
    if (hasPermission) {
      console.log('Loading data due to permission');
      loadServices(); // Load services on component mount
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      console.log('No permission to view page');
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadServices();
    }
  }, [hasAppliedFilter]);

  const loadServices = async (filterToUse = null) => {
    try {
      setLoading(true);
      const finalFilter = filterToUse || (hasAppliedFilter ? filter : {});
      
      console.log('Loading services with filter:', finalFilter);
      const response = await getServices(finalFilter);
      
      if (Array.isArray(response)) {
        setServices(response);
        setTotalCount(response.length);
      } else {
        console.error('Expected array response but got:', response);
        setServices([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Error loading services:", error);
      Swal.fire({
        title: "Error!",
        text: "Failed to load services. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
      setServices([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (service) => {
    try {
      setEditLoadingRow(service.service_Id);
      
      const session = getUserSession();
      const editData = {
        ...service,
        modified_By: session.userId,
        modified_Date: new Date().toISOString(),
      };
      
      setForm(editData);
      setIsEditing(true);
      setActiveTab("1");
    } catch (error) {
      console.error("Error setting up edit:", error);
    } finally {
      setEditLoadingRow(null);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaveLoading(true);
      const session = getUserSession();
      
      const dataToSave = {
        ...form,
        created_By: isEditing ? form.created_By : session.userId,
        modified_By: session.userId,
        created_Date: isEditing ? form.created_Date : new Date().toISOString(),
        modified_Date: new Date().toISOString(),
      };

      console.log('Saving service:', dataToSave);
      const response = await saveService(dataToSave);
      
      console.log('Save response:', response);
      
      Swal.fire({
        title: "Success!",
        text: `Service ${isEditing ? 'updated' : 'created'} successfully!`,
        icon: "success",
        confirmButtonText: "OK",
      });

      // Reset form and reload data
      handleClear();
      await loadServices();
      
    } catch (error) {
      console.error("Error saving service:", error);
      
      let errorMessage = "Failed to save service. Please try again.";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      Swal.fire({
        title: "Error!",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleClear = () => {
    setForm(defaultForm);
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
    setPage(1);
    loadServices(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setHasAppliedFilter(false);
    setServices([]);
    setTotalCount(0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const getFilteredServices = () => {
    if (!hasAppliedFilter) return services;
    
    return services.filter(service => {
      const nameMatch = !filter.service_NameEN || 
        service.service_NameEN.toLowerCase().includes(filter.service_NameEN.toLowerCase());
      const statusMatch = filter.status === null || service.status === filter.status;
      return nameMatch && statusMatch;
    });
  };

  const getPaginatedServices = () => {
    const filteredServices = getFilteredServices();
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredServices.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(totalCount / pageSize);
  };

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      // Refresh services when switching to Service List tab
      if (tab === "2") {
        loadServices();
      }
    }
  };

  const getServiceNameOptions = () => {
    return services.map(service => ({
      value: service.service_NameEN,
      label: service.service_NameEN
    }));
  };

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Service Management" breadcrumbItem="Spa" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {!isViewOnly && (
                    <NavItem>
                      <NavLink
                        style={{ 
                          cursor: "pointer",
                          fontWeight: activeTab === "1" ? "bold" : "normal",
                          color: activeTab === "1" ? "#556ee6" : "#74788d",
                          borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                          padding: "15px 25px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => toggle("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Service Form</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => toggle("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Service List</span>
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} className="p-4">
                  {!isViewOnly && (
                    <TabPane tabId="1">
                      <div>
                        <Form onSubmit={handleSubmit}>
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="service_NameEN">Service Name (English) <span className="text-danger">*</span></Label>
                              <Input
                                type="text"
                                id="service_NameEN"
                                value={form.service_NameEN}
                                onChange={(e) => handleFormChange("service_NameEN", e.target.value)}
                                placeholder="Enter service name in English"
                                required
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="service_NameAR">Service Name (Arabic)</Label>
                              <Input
                                type="text"
                                id="service_NameAR"
                                value={form.service_NameAR}
                                onChange={(e) => handleFormChange("service_NameAR", e.target.value)}
                                placeholder="Enter service name in Arabic"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={8}>
                            <FormGroup className="mb-4">
                              <Label for="description">Description</Label>
                              <Input
                                type="textarea"
                                id="description"
                                value={form.description}
                                onChange={(e) => handleFormChange("description", e.target.value)}
                                placeholder="Enter service description"
                                rows="3"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup className="mb-4">
                              <div className="form-check form-switch">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="service_status"
                                  name="status"
                                  checked={form.status}
                                  onChange={(e) => handleFormChange("status", e.target.checked)}
                                  disabled={!hasDeletePermission}
                                />
                                <label className="form-check-label" htmlFor="service_status">
                                  {form.status ? "Active" : "Inactive"}
                                </label>
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row className="mt-4">
                          <Col>
                            <div className="d-flex gap-2">
                              <Button
                                type="submit"
                                color="primary"
                                disabled={saveLoading}
                                className="waves-effect waves-light"
                              >
                                {saveLoading && (
                                  <ClipLoader size={16} color="#ffffff" className="me-2" />
                                )}
                                {isEditing ? "Update Service" : "Save Service"}
                              </Button>
                              <Button
                                type="button"
                                color="secondary"
                                onClick={handleClear}
                                className="waves-effect waves-light"
                              >
                                Clear
                              </Button>
                            </div>
                          </Col>
                        </Row>
                        </Form>
                      </div>
                    </TabPane>
                  )}

                  <TabPane tabId="2">
                    <div>
                      {/* Filter Section */}
                      <Card className="mb-4">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-3">Filter Services</h5>
                            <Button
                              style={{
                                backgroundColor: '#556ee6',
                                borderColor: '#556ee6',
                                color: 'white',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>
                          {showFilters && (
                            <Row className="g-3">
                              <Col md={4}>
                                <Label for="filterServiceName">Service Name</Label>
                                <Select
                                  id="filterServiceName"
                                  value={getServiceNameOptions().find(option => option.value === filter.service_NameEN)}
                                  onChange={(selectedOption) => 
                                    handleFormChange("service_NameEN", selectedOption ? selectedOption.value : "")
                                  }
                                  options={getServiceNameOptions()}
                                  isClearable
                                  placeholder="Select service name"
                                  className="react-select"
                                  classNamePrefix="react-select"
                                />
                              </Col>
                              <Col md={4}>
                                <Label for="filterStatus">Status</Label>
                                <Select
                                  id="filterStatus"
                                  value={statusOptions.find(option => option.value === filter.status)}
                                  onChange={(selectedOption) => 
                                    setFilter(prev => ({ ...prev, status: selectedOption ? selectedOption.value : null }))
                                  }
                                  options={statusOptions}
                                  isClearable
                                  placeholder="Select status"
                                  className="react-select"
                                  classNamePrefix="react-select"
                                />
                              </Col>
                              <Col md={4} className="d-flex align-items-end">
                                <div className="d-flex gap-2">
                                  <Button 
                                    color="primary" 
                                    onClick={handleApplyFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Apply Filter
                                  </Button>
                                  <Button 
                                    color="secondary" 
                                    onClick={handleClearFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Clear Filter
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          )}
                        </CardBody>
                      </Card>

                      {/* Service List */}
                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">Service List</h5>
                            {!isViewOnly && (
                              <Button
                                color="primary"
                                onClick={() => toggle("1")}
                                className="waves-effect waves-light"
                              >
                                <i className="bx bx-plus me-1"></i>
                                Add New Service
                              </Button>
                            )}
                          </div>

                          {loading ? (
                            <div className="text-center p-4">
                              <ClipLoader size={50} color="#556ee6" />
                              <p className="mt-2">Loading services...</p>
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table className="table table-bordered table-striped table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="5%">#</th>
                                      <th width="20%">Service Name (EN)</th>
                                      <th width="20%">Service Name (AR)</th>
                                      <th width="25%">Description</th>
                                      <th width="10%">Status</th>
                                      <th width="10%">Created Date</th>
                                      <th width="10%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {getPaginatedServices().length > 0 ? (
                                      getPaginatedServices().map((service, index) => (
                                        <tr key={service.service_Id}>
                                          <td>{(page - 1) * pageSize + index + 1}</td>
                                          <td>{service.service_NameEN}</td>
                                          <td>{service.service_NameAR || 'N/A'}</td>
                                          <td>{service.description || 'N/A'}</td>
                                          <td>
                                            <span className={`badge ${service.status ? 'bg-success' : 'bg-danger'}`}>
                                              {service.status ? 'Active' : 'Inactive'}
                                            </span>
                                          </td>
                                          <td>{formatDateTime(service.created_Date)}</td>
                                          <td>
                                            {!isViewOnly && (
                                              <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => handleEdit(service)}
                                                disabled={editLoadingRow === service.service_Id}
                                                className="waves-effect waves-light"
                                              >
                                                {editLoadingRow === service.service_Id ? (
                                                  <ClipLoader size={12} color="#ffffff" />
                                                ) : (
                                                  <SquarePen size={12} />
                                                )}
                                              </Button>
                                            )}
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="7" className="text-center">
                                          {hasAppliedFilter ? "No services found matching the filter criteria." : "No services available. Please add some services first."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>

                              {/* Pagination */}
                              {getTotalPages() > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                  <div>
                                    <p className="text-muted mb-0">
                                      Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
                                    </p>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page - 1)}
                                      disabled={page === 1}
                                    >
                                      Previous
                                    </Button>
                                    <span className="align-self-center mx-2">
                                      Page {page} of {getTotalPages()}
                                    </span>
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() => handlePageChange(page + 1)}
                                      disabled={page === getTotalPages()}
                                    >
                                      Next
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
